import { Controller } from "@hotwired/stimulus";

// On the etude "Add contact" modal, the entreprise field is contextual: a
// contact may now be linked to several entreprises and we want to pick which
// one is being represented on this particular étude. The contact list ships
// with a JSON payload mapping contact_id → list of {id, nom, principal,
// poste}; this controller rebuilds the entreprise <select> whenever the
// contact picker changes, with the contact's principal employment preselected.
export default class extends Controller {
  static targets = ["contactSelect", "entrepriseSelect", "entrepriseFieldWrap"];
  static values  = { payload: Object };

  connect() {
    if (!this.hasContactSelectTarget || !this.hasEntrepriseSelectTarget) return;
    this._handler = () => this._refresh();
    this.contactSelectTarget.addEventListener("change", this._handler);
    // Select2 fires its events on the underlying <select>, so the same
    // listener catches both native and jQuery-driven updates.
    this._refresh();
  }

  disconnect() {
    if (this._handler && this.hasContactSelectTarget) {
      this.contactSelectTarget.removeEventListener("change", this._handler);
    }
  }

  _refresh() {
    const contactId = this.contactSelectTarget.value;
    const sel = this.entrepriseSelectTarget;
    const wrap = this.entrepriseFieldWrapTarget;

    sel.innerHTML = "";

    if (!contactId) {
      wrap.hidden = true;
      return;
    }

    const rows = (this.payloadValue || {})[contactId] || [];
    if (rows.length === 0) {
      wrap.hidden = true;
      return;
    }

    // The contact picker can carry a "suggested" entreprise id on each
    // option — when a multi-entreprise contact is picked from the
    // HEX-Agency optgroup vs his primary employer's optgroup, we want
    // HEX-Agency pre-selected here. Falls back to the contact's principal
    // when the suggestion isn't present.
    const pickedOption = this.contactSelectTarget.selectedOptions[0];
    const suggested = pickedOption?.dataset?.suggestedEntrepriseId;

    // Single-option fast path: hide the picker and ship the value silently.
    if (rows.length === 1) {
      const opt = new Option(rows[0].nom, rows[0].id, true, true);
      sel.appendChild(opt);
      wrap.hidden = true;
      return;
    }

    rows.forEach((row) => {
      const label = row.principal
        ? `${row.nom} · principale${row.poste ? ` — ${row.poste}` : ""}`
        : `${row.nom}${row.poste ? ` — ${row.poste}` : ""}`;
      const isSuggested = suggested && String(suggested) === String(row.id);
      const isPreselected = isSuggested || (!suggested && !!row.principal);
      const opt = new Option(label, row.id, false, isPreselected);
      sel.appendChild(opt);
    });

    wrap.hidden = false;
  }
}
